import React, { Fragment, useEffect, useState, useCallback } from "react";
import {
  Box,
  Flex,
  HStack,
  Text,
  Button,
  VStack,
  Heading,
  IconButton,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Container,
} from "@chakra-ui/react";
import { BiChevronLeft, BiEditAlt, BiCheck, BiX } from "react-icons/bi";
import NokDisplay from "../components/RequestDocument/NokDisplay";
import Documents from "../components/RequestDocument/Documents";
import {
  getInstantLoanRequestDetails,
  getUserDetails,
  commentLogs,
} from "../services/axios";
import { useParams, useNavigate, Link } from "react-router-dom";
import TransactionLog from "../components/RequestDocument/TransactionLog";
import InstantLoanBorrowersInformation from "../components/InstantRequestDocument/InstantLoanBorrowersInformation";
import EmployerInformation from "../components/InstantRequestDocument/EmployerInformation";
import EditLoanModal from "../components/Modals/EditLoanModal";
import ConfirmationModal from "../components/Modals/ConfirmationModal";
import RejectInstantLoan from "../components/Modals/RejectInstantLoan";
import SuccessModal from "../components/Modals/SuccessModal";
import Loader from "../components/Loader/loader";
import ConfirmInstantLoanApproval from "../components/Modals/ConfirmInstantLoanApproval";
import CreditRecordScoring from "../components/RequestDocument/CreditRecordScoring";
import { formatMoney } from "../services/utils/helpersFunc";
import LogLoanModal from "../components/RequestDocument/LogLoanModal";
import InstantLoanOfferDetails from "../components/RequestDocument/LoanOfferDetails";
import InstantLoanApprovalWorkFlow from "../components/RequestDocument/InstantLoanApprovalWorkFlow";
import RepaymentSchedule from "../components/RequestDocument/RepaymentSchedule";
import { LoanStatusBadge } from "../components/ListofLoans";
import { motion } from "framer-motion";

const MotionBox = motion(Box);

const InstantLoanDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loanRequest, setLoanRequest] = useState(null);
  const [editLoan, setEditLoan] = useState(0);
  const [rejectLoan, setRejectLoan] = useState(false);
  const [newAmount, setNewAmount] = useState();
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [limit, setLimit] = useState(10);
  const [processing, setProcessing] = useState(false);
  const [loanLogs, setLoanLogs] = useState([]);

  const getLogs = useCallback(async (theLimit) => {
    setProcessing(true);
    const { data } = await commentLogs(id, theLimit);
    if (data) setLoanLogs(data?.data);
    setProcessing(false);
  }, [id]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await getInstantLoanRequestDetails(id);
        if (res?.data) {
          const data = res.data;
          setLoanRequest(data);
          
          // Load secondary data without blocking
          getUserDetails(data.user_id).then((userRes) => {
            if (userRes?.data) setUser(userRes.data);
          });
          getLogs(limit);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, getLogs, limit]);

  if (loading) {
    return (
      <Flex justify="center" align="center" h="80vh">
        <Loader />
      </Flex>
    );
  }

  if (!loanRequest) {
    return (
      <Flex justify="center" align="center" h="80vh">
        <Text fontSize="lg" color="gray.500">Loan request not found.</Text>
      </Flex>
    );
  }

  return (
    <Box bg="gray.50" minH="100vh">
      <EditLoanModal
        loanRequest={loanRequest}
        isOpen={editLoan === 1}
        setIsOpen={setEditLoan}
        setNewAmount={setNewAmount}
        newAmount={newAmount}
      />

      <RejectInstantLoan
        loanRequest={loanRequest}
        isOpen={rejectLoan}
        setIsOpen={setRejectLoan}
        setEditLoan={setEditLoan}
      />

      {newAmount && (
        <ConfirmationModal
          loanRequest={loanRequest}
          isOpen={editLoan === 2}
          setIsOpen={setEditLoan}
          newAmount={newAmount}
        />
      )}

      <SuccessModal
        isOpen={editLoan === 3}
        setIsOpen={setEditLoan}
        loanRequest={loanRequest}
      />

      <ConfirmInstantLoanApproval
        loanRequest={loanRequest}
        isOpen={editLoan === 4}
        setIsOpen={setEditLoan}
      />

      <Box bg="white" borderBottom="1px solid" borderColor="gray.100" py={4} position="sticky" top={0} zIndex={10}>
        <Container maxW="container.xl">
          <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
            <VStack align="flex-start" spacing={1}>
              <HStack spacing={2} color="gray.500" fontSize="xs">
                <IconButton 
                  icon={<BiChevronLeft size={20} />} 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigate(-1)}
                  aria-label="Back"
                />
                <Breadcrumb separator={<Text color="gray.300">/</Text>}>
                  <BreadcrumbItem>
                    <BreadcrumbLink as={Link} to="/dashboard">Dashboard</BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbItem isCurrentPage>
                    <BreadcrumbLink fontWeight="bold" color="brand.500">Instant Loan Details</BreadcrumbLink>
                  </BreadcrumbItem>
                </Breadcrumb>
              </HStack>
              <HStack spacing={3}>
                <Heading size="md" color="brand.900">
                  {loanRequest?.first_name} {loanRequest?.last_name}
                </Heading>
                <LoanStatusBadge status={loanRequest?.admin_approval || "pending"} />
              </HStack>
            </VStack>

            <HStack spacing={3}>
              {(!loanRequest?.admin_approval || loanRequest.admin_approval === "pending") ? (
                <>
                  <Button
                    leftIcon={<BiCheck />}
                    colorScheme="green"
                    size="md"
                    onClick={() => setEditLoan(4)}
                  >
                    APPROVE
                  </Button>
                  <Button
                    leftIcon={<BiEditAlt />}
                    variant="outline"
                    colorScheme="brand"
                    size="md"
                    onClick={() => setEditLoan(1)}
                  >
                    EDIT
                  </Button>
                  <Button
                    leftIcon={<BiX />}
                    colorScheme="red"
                    size="md"
                    onClick={() => setRejectLoan(true)}
                  >
                    REJECT
                  </Button>
                </>
              ) : (
                <Box bg="gray.50" px={4} py={2} borderRadius="md" border="1px solid" borderColor="gray.200">
                  <Text fontWeight="700" color="gray.700" fontSize="sm">
                    {loanRequest.admin_approval === "approved" ? (
                      <>APPROVED: ₦{formatMoney(Number(loanRequest?.final_amount || loanRequest?.credit_recommended_amount || loanRequest?.amount))}</>
                    ) : (
                      <Text color="red.500">DECISION: {loanRequest?.reason || loanRequest?.credit_recommendation_reason || loanRequest.admin_approval.toUpperCase()}</Text>
                    )}
                  </Text>
                </Box>
              )}
            </HStack>
          </Flex>
        </Container>
      </Box>

      <Container maxW="container.xl" py={8}>
        <VStack spacing={8} align="stretch">
          <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <InstantLoanBorrowersInformation loanRequest={loanRequest} user={user} />
          </MotionBox>

          <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <CreditRecordScoring loanRequest={loanRequest} />
          </MotionBox>

          <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <EmployerInformation loanRequest={loanRequest} />
          </MotionBox>

          <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <NokDisplay loanRequest={loanRequest} />
          </MotionBox>

          <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <Documents loanRequest={loanRequest} user={user} />
          </MotionBox>

          <InstantLoanApprovalWorkFlow loanRequest={loanRequest} />
          
          <LogLoanModal
            processing={processing}
            getLogs={getLogs}
            loanLogs={loanLogs}
            limit={limit}
            setLimit={setLimit}
          />

          {loanRequest?.loan && (
            <InstantLoanOfferDetails loanRequest={loanRequest} />
          )}

          {loanRequest?.repaymentSchedule?.length > 0 && (
            <RepaymentSchedule loanRequest={loanRequest} />
          )}

          {loanRequest?.transactions?.length > 0 && (
            <TransactionLog loanRequest={loanRequest} />
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default InstantLoanDetails;
